import React, { useEffect, useState } from 'react';
import { Pencil, ExternalLink, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { slugify } from '@/data/nav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageField } from '@/components/common/ImageUploadField';

const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';
const labelCls = 'text-xs font-medium text-muted-foreground';

const EMPTY = {
  title: '', slug: '', content: '',
  meta_title: '', meta_description: '', keywords: '', canonical_url: '',
  og_title: '', og_description: '', og_image: '',
};

const toForm = (p) => ({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])) });

export default function AdminPages() {
  const [pages, setPages] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('general');
  const [busy, setBusy] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const load = () => api.get('/admin/pages').then((r) => setPages(r.data)).catch(() => toast.error('Could not load pages'));
  useEffect(() => { load(); }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const openEdit = (p) => { setEditing(p); setForm(toForm(p)); setTab('general'); setIsNew(false); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setTab('general'); setIsNew(true); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    const slug = slugify(form.slug || form.title);
    if (!slug) { toast.error('A valid slug is required'); return; }
    setBusy(true);
    try {
      await api.put(`/admin/pages/${slug}`, { ...form, slug });
      toast.success('Page saved');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Pages</h1>
          <p className="text-sm text-muted-foreground">Edit your policy & content pages (About, Contact, FAQ, Shipping, Returns, Privacy, Terms, Cancellation) with full SEO control.</p>
        </div>
        <button onClick={openNew} data-testid="admin-pages-add-btn" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white"><Plus size={15} /> New Page</button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-pages-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Page</th><th className="p-3">URL</th><th className="p-3">SEO title</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {pages?.map((p) => (
              <tr key={p.slug} className="border-t border-border" data-testid="admin-pages-row">
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3 text-[11px] text-muted-foreground">/page/{p.slug}</td>
                <td className="p-3 text-xs text-muted-foreground">{p.meta_title || <span className="italic opacity-60">— using default —</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <a href={`/page/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-foreground" title="View"><ExternalLink size={13} /></a>
                    <button onClick={() => openEdit(p)} data-testid="admin-pages-edit-btn" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><Pencil size={12} /> Edit</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages && pages.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No pages yet.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit: ${editing.title}` : 'New Page'}</DialogTitle>
            <DialogDescription>Content and SEO for this page. Basic HTML is supported in the content.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {['general', 'seo'].map((s) => (
              <button key={s} onClick={() => setTab(s)} className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize ${tab === s ? 'bg-[hsl(var(--primary))] text-white' : 'border border-border'}`} data-testid={`admin-pages-tab-${s}`}>{s === 'seo' ? 'SEO & Social' : s}</button>
            ))}
          </div>
          {tab === 'general' && (
            <div className="grid gap-3">
              <div><label className={labelCls}>Title *</label><input value={form.title} onChange={(e) => update({ title: e.target.value })} className={field} data-testid="admin-pages-title" /></div>
              <div>
                <label className={labelCls}>Slug</label>
                <input value={form.slug} onChange={(e) => update({ slug: slugify(e.target.value) })} disabled={!isNew} className={`${field} ${!isNew ? 'opacity-60' : ''}`} data-testid="admin-pages-slug" />
                <p className="mt-1 text-[11px] text-muted-foreground">/page/{slugify(form.slug || form.title) || '...'}</p>
              </div>
              <div>
                <label className={labelCls}>Content</label>
                <textarea rows={14} value={form.content} onChange={(e) => update({ content: e.target.value })} className={field} placeholder={'Write the page content. Separate paragraphs with a blank line.\nBasic HTML is supported (<h2>, <p>, <ul>, <a>...).'} data-testid="admin-pages-content" />
              </div>
            </div>
          )}
          {tab === 'seo' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={labelCls}>SEO title</label><input value={form.meta_title} onChange={(e) => update({ meta_title: e.target.value })} className={field} placeholder={`${form.title || 'Page title'} · Gaze Decor`} data-testid="admin-pages-meta-title" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Meta description</label><textarea rows={2} value={form.meta_description} onChange={(e) => update({ meta_description: e.target.value })} className={field} placeholder="~150 characters shown in search results" data-testid="admin-pages-meta-desc" /></div>
              <div><label className={labelCls}>Keywords</label><input value={form.keywords} onChange={(e) => update({ keywords: e.target.value })} className={field} placeholder="comma, separated" data-testid="admin-pages-keywords" /></div>
              <div><label className={labelCls}>Canonical URL</label><input value={form.canonical_url} onChange={(e) => update({ canonical_url: e.target.value })} className={field} placeholder="https://..." data-testid="admin-pages-canonical" /></div>
              <div><label className={labelCls}>OG title</label><input value={form.og_title} onChange={(e) => update({ og_title: e.target.value })} className={field} data-testid="admin-pages-og-title" /></div>
              <div><label className={labelCls}>OG description</label><input value={form.og_description} onChange={(e) => update({ og_description: e.target.value })} className={field} data-testid="admin-pages-og-desc" /></div>
              <div className="sm:col-span-2"><ImageField label="OG / Social share image" value={form.og_image} onChange={(v) => update({ og_image: v })} testid="admin-pages-og-image" previewClass="max-w-[280px]" /></div>
            </div>
          )}
          <button onClick={save} disabled={busy} data-testid="admin-pages-save-btn" className="w-full rounded-full bg-[hsl(var(--primary))] py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Saving...' : 'Save Page'}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
